import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { Markdown } from '@dadohr/shared/domainTypes/markdown';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormHelperText,
  List,
  ListItem,
  TextField,
  TextFieldProps,
  Typography
} from '@material-ui/core';
import MDEditor, {
  commands,
  ContextStore,
  ICommand,
  TextAreaTextApi,
  TextState
} from '@uiw/react-md-editor';
import assertNever from 'assert-never';
import { groupBy, throttle } from 'lodash';
import { HelpCircle, PlusSquare } from 'lucide-react';
// @ts-ignore
import _truncateMarkdown from 'markdown-truncate';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
// import remarkTokens from './remarkTokens';
import remarkDirective from 'remark-directive';
import remarkDirectiveRehype from 'remark-directive-rehype';
import remarkEmoji from 'remark-emoji';
import { useDialogState } from '../../hooks/useDialogState';
import { useTranslation, useUnsafeTranslation } from '../../i18next';
import { FlexContainer } from '../../layout/Flex';
import { toOrdinal } from '../../services/numbers';
import { toSearchTermRegexp } from '../../services/user';
import { Color, ColoredChip, getColor, getColorLight } from '../ColoredChip';
import { CopyButton } from '../CopyButton';
import { DialogTitleWithTypography } from '../DialogTitleWithTypography';
import { FormRow } from '../Form/FormRow';

const MarkdownContainer = styled('div')<{
  textAlign?: 'left' | 'center' | 'right';
}>((p) => ({
  '>*': {
    margin: 0,
    '&:not(:last-child)': {
      marginBottom: p.theme.spacing(2)
    }
  },
  a: {
    color: p.theme.custom.colors.primary.main
  },
  img: { display: 'block', maxWidth: '100%' },
  // Fill the parent so `textAlign` (center/right) is honoured even when this is a
  // shrink-wrapping flex child. The employee hub renders content blocks as direct
  // children of a `flex-start` column (MessageBody's FlexContainerVertical), where a
  // width-less container collapses to its content width and a centered caption reads
  // as left-aligned. The experience-builder preview uses a `stretch` column, so it
  // looked correct there. Mirrors sibling content blocks (ImageContainer, Divider)
  // which already set width:100%. No-op in normal block flow.
  width: '100%',
  textAlign: p.textAlign
}));

const MarkdownCode = styled('code')((p) => ({
  fontSize: p.theme.custom.fontSize.xs,
  backgroundColor: p.theme.custom.colors.gray.w100,
  borderRadius: p.theme.custom.border.radius(),
  padding: p.theme.spacing(0.5)
}));

const MarkdownPre = styled('pre')((p) => ({
  display: 'block',
  width: '100%',
  backgroundColor: p.theme.custom.colors.gray.w100,
  padding: p.theme.spacing(1)
}));

const MarkdownQuote = styled('div')((p) => ({
  display: 'block',
  paddingLeft: p.theme.spacing(),
  borderLeft: `2px solid ${p.theme.custom.colors.gray.w300}`,
  color: p.theme.custom.colors.gray.w700
}));

export const MarkdownText = React.memo(
  ({
    t,
    throttleMs = 0,
    renderToken
  }: {
    t: Markdown;
    throttleMs?: number;
    renderToken?: (token: string, opts: string) => React.ReactNode;
  }) => {
    const text = t.t;
    const [value, setValue] = useState(text);
    const throttledSetValue = useMemo(
      () => (throttleMs ? throttle(setValue, throttleMs) : setValue),
      [throttleMs]
    );

    useEffect(() => {
      throttledSetValue(text);
    }, [text, throttledSetValue]);

    const { plugins, components } = useMemo(() => {
      return {
        components: {
          ...{
            h1: 'p',
            h2: 'p',
            h3: 'p',
            h4: 'p',
            h5: 'p',
            h6: 'p',
            hr: () => {
              return <Divider light />;
            },
            pre: ({ children }: { children: any }) => {
              return <MarkdownPre>{children}</MarkdownPre>;
            },
            code: ({ children }: { children: any }) => {
              return <MarkdownCode>{children}</MarkdownCode>;
            },
            blockquote: ({ children }: { children: any }) => {
              return <MarkdownQuote>{children}</MarkdownQuote>;
            }
          },
          // Splitting this out to trick the type system
          ...(renderToken
            ? {
                token: ({ children }: { children?: [string] }) => {
                  const token = children?.[0]?.trim() || '';
                  const [key, opts] = token.split('|').map((x) => x.trim());
                  return renderToken(key, opts) || token;
                }
              }
            : {})
        },
        plugins: renderToken
          ? [remarkBreaks, remarkEmoji, remarkDirective, remarkDirectiveRehype]
          : [remarkBreaks, remarkEmoji]
      };
    }, [renderToken]);

    return (
      <MarkdownContainer textAlign={t.textAlign || 'left'}>
        <ReactMarkdown
          components={components as any}
          remarkPlugins={plugins}
          linkTarget={'_blank'}
        >
          {renderToken
            ? (value ?? '').replace(/{{(.*?)}}/g, (m, m1: string) => {
                return `:token[${m1}]`;
              })
            : (value ?? '')}
        </ReactMarkdown>
      </MarkdownContainer>
    );
  }
);

export const Token: React.FC<{ color: Color; mode?: 'light' | 'dark' }> = ({
  color,
  children,
  mode = 'light'
}) => {
  const theme = useTheme();
  const borderRadius = theme.custom.border.radius();
  const padding = `1px ${theme.spacing(0.5)}px`;
  const fontSize = `0.835em`;
  const [fgColor, bgColor] =
    mode === 'dark' ? getColor(theme, color) : getColorLight(theme, color);
  return (
    <span
      style={{
        color: fgColor,
        background: bgColor,
        borderRadius,
        padding,
        fontSize
      }}
    >
      {children}
    </span>
  );
};

export const MarkdownTextWithReplacementPlaceholders = ({
  t,
  throttleMs,
  tokens
}: {
  t: Markdown;
  throttleMs?: number;
  tokens: TokenDefinition[];
}) => {
  const renderToken = useCallback(
    (key: string, opts: string) => {
      const token = tokens.find((tk) => tk.token === key);
      if (token) {
        return (
          <Token color={token.color}>
            {token.group}: {token.label}
            {opts && ` | ${opts}`}
          </Token>
        );
      }
      return (
        <Token color="red" mode="dark">
          Unknown token: {key}
        </Token>
      );
    },
    [tokens]
  );
  return (
    <MarkdownText t={t} throttleMs={throttleMs} renderToken={renderToken} />
  );
};

export const PlainTextWithReplacementPlaceholders = ({
  text,
  tokens,
  textAlign = 'left'
}: {
  text: string;
  tokens: TokenDefinition[];
  textAlign?: 'left' | 'center' | 'right';
}) => {
  // This banks a bit on the fact that no other funny markdown
  // syntax is present in the text given, as it's a bit tricky
  // to disable this properly.
  // We might eventually add custom parsing to this and
  // just return a bunch of spans without going through markdown.
  const t = useMemo(() => ({ t: text, v: 1, textAlign }), [text, textAlign]);
  return <MarkdownTextWithReplacementPlaceholders t={t} tokens={tokens} />;
};

const Grid = styled('div')`
  display: grid;
  margin-top: ${(p) => p.theme.spacing(4)}px;
  grid-row-gap: 1px;
  grid-column-gap: 0;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  background-color: ${(p) => p.theme.custom.colors.gray.w200};

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    grid-gap: 0;
  }

  ol,
  ul {
    padding: 0;
    margin: 0;
    margin-left: ${(p) => p.theme.spacing(2)}px;
  }
`;

const GridItem = styled('div')`
  display: flex;
  align-self: stretch;
  align-items: center;
  background-color: ${(p) => p.theme.custom.colors.background.light};
  padding: ${(p) => p.theme.spacing(2)}px ${(p) => p.theme.spacing(4)}px
    ${(p) => p.theme.spacing(2)}px 0;
  @media (max-width: 500px) {
    &:nth-child(2n + 1) {
      padding-bottom: 0;
    }
  }
`;

const toMarkdown = (t: string) => ({ v: 1, t });

const MarkdownSyntaxDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitleWithTypography>
        <div>{t('markdownCheatsheet.title')}</div>
      </DialogTitleWithTypography>
      <DialogContent>
        <Typography variant="body1" component="p">
          {t('markdownCheatsheet.intro')}
        </Typography>
        <Grid>
          <GridItem>
            <MarkdownPre>
              <MarkdownCode>
                {t('markdownCheatsheet.boldAndItalic')}
              </MarkdownCode>
            </MarkdownPre>
          </GridItem>
          <GridItem>
            <MarkdownText
              t={toMarkdown(t('markdownCheatsheet.boldAndItalic'))}
            />
          </GridItem>
          <GridItem>
            <MarkdownPre>
              <MarkdownCode>
                - {t('markdownCheatsheet.listItem')} <br />-{' '}
                {t('markdownCheatsheet.otherListItem')} <br />
                &nbsp;&nbsp;- {t('markdownCheatsheet.otherListItem')}
              </MarkdownCode>
            </MarkdownPre>
          </GridItem>
          <GridItem>
            <MarkdownText
              t={toMarkdown(
                `- ${t('markdownCheatsheet.listItem')} \n- ${t(
                  'markdownCheatsheet.otherListItem'
                )} \n  - ${t('markdownCheatsheet.otherListItem')}`
              )}
            />
          </GridItem>
          <GridItem>
            <MarkdownPre>
              <MarkdownCode>
                1. {t('markdownCheatsheet.listItem')} <br /> 2.{' '}
                {t('markdownCheatsheet.otherListItem')}
              </MarkdownCode>
            </MarkdownPre>
          </GridItem>
          <GridItem>
            <MarkdownText
              t={toMarkdown(
                `1. ${t('markdownCheatsheet.listItem')} \n2. ${t(
                  'markdownCheatsheet.otherListItem'
                )}`
              )}
            />
          </GridItem>
          <GridItem>
            <MarkdownPre>
              <MarkdownCode>[Dado HR](https://dadohr.com)</MarkdownCode>
            </MarkdownPre>
          </GridItem>
          <GridItem>
            <MarkdownText t={toMarkdown('[Dado HR](https://dadohr.com)')} />
          </GridItem>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={() => {
            onClose();
          }}
        >
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const updateMarkdown = (oldValue: Markdown, nextText: string) =>
  Object.assign({}, oldValue, { t: nextText });

export const MarkdownTextField = (
  props: TextFieldProps & {
    noMarkdownHint?: boolean;
  }
) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const { t } = useTranslation();

  const ButtonWithSmallFont = styled(Button)`
    font-size: ${(p) => p.theme.custom.fontSize.s}px!important;
  `;

  return (
    <>
      <TextField variant="outlined" multiline {...props} />
      {!props.noMarkdownHint && (
        <>
          <FormHelperText style={{ textAlign: 'right' }}>
            <ButtonWithSmallFont
              startIcon={<HelpCircle size={14} />}
              size="small"
              onClick={openDialog}
            >
              {t('markdownCheatsheet.helperText')}
            </ButtonWithSmallFont>
          </FormHelperText>
          <MarkdownSyntaxDialog open={dialogOpen} onClose={closeDialog} />
        </>
      )}
    </>
  );
};

const ToolbarIconLabelWithIcon = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing(0.5)
}));

type MarkdownEditorProps = {
  value: string;
  onChange: (nextValue: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  rows?: number;
  onBlur?: (ev: React.FocusEvent<HTMLDivElement>) => void;
  fullWidth?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  // NEW_UX inline editor: borderless body + light toolbar that only appears while
  // editing (float-on-focus), matching the task-editor prototype's RichToolbar look.
  // Same markdown commands — reskin only. Opt-in; classic path unchanged.
  newUx?: boolean;
  placeholder?: string;
  // NEW_UX only: lets a Callout block's inline editor match the block's own color
  // (classic keeps the plain white editor background regardless of block type).
  backgroundColor?: string;
};

const TokenGroup = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const TokenGroupHeading = styled('div')((p) => ({
  marginBottom: p.theme.spacing(1)
}));

const TokenSearch = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const EmptyTokenGroups = styled('div')((p) => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: p.theme.custom.colors.gray.w400,
  fontSize: p.theme.custom.fontSize.s,
  minHeight: 50
}));

const FormatItem = styled('div')`
  display: flex;
  justify-content: stretch;
  padding: ${(p) => p.theme.spacing(1.5)}px ${(p) => p.theme.spacing(1)}px;
  cursor: pointer;
  &:not(:last-of-type) {
    border-bottom: 1px solid ${(p) => p.theme.custom.colors.gray.w200};
  }
  &:hover {
    background-color: ${(p) => p.theme.custom.colors.gray.w100};
  }
  &:active {
    background-color: ${(p) => p.theme.custom.colors.gray.w200};
  }
`;

const FormatGroup = styled('div')`
  margin: ${(p) => p.theme.spacing(1)}px 0;
`;

const DATE_TIME_FORMATS = [
  {
    formats: ['MMM DD YYYY', 'MMMM Do, YYYY', 'dddd MMMM Do, YYYY']
  },
  {
    formats: ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY']
  },
  {
    formats: ['YYYY.MM.DD', 'DD.MM.YYYY', 'MM.DD.YYYY']
  },
  {
    formats: ['YYYY/MM/DD', 'DD/MM/YYYY', 'MM/DD/YYYY']
  }
];

export type TokenDefinition = {
  label: string;
  group: string;
  token: string;
  color: Color;
  type: CustomUserField['type'];
};

export const TokenSelectorDialog = ({
  open,
  onClose,
  tokens,
  onSelect
}: {
  open: boolean;
  onClose: () => void;
  tokens: TokenDefinition[];
  onSelect: (def: TokenDefinition, opts: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState<
    | {
        page: 'LIST';
      }
    | {
        page: 'DATE';
        t: TokenDefinition;
      }
    | {
        page: 'NUMBER';
        t: TokenDefinition;
      }
  >({ page: 'LIST' });

  const [q, setQ] = useState('');
  useEffect(() => {
    open && setQ('');
  }, [open]);

  const tokenGroups = useMemo(() => {
    const re = toSearchTermRegexp(q);
    return Object.entries(
      groupBy(
        tokens.filter((t) => {
          // TODO use fuzzy matching, so that i can do pafi to find participant.firstName
          return t.token.match(re) || t.label.match(re);
        }),
        (t) => t.group
      )
    ).map(([group, ts]) => ({ key: group, label: group, tokens: ts }));
  }, [q, tokens]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {state.page === 'LIST'
          ? t('Insert a text variable')
          : state.page === 'DATE'
          ? t('Choose a date format')
          : state.page === 'NUMBER'
          ? t('Choose a number format')
          : assertNever(state)}
      </DialogTitleWithTypography>
      {state.page === 'LIST' && (
        <>
          <DialogContent>
            <TokenSearch>
              <TextField
                type="search"
                fullWidth
                autoFocus
                value={q}
                onChange={(ev) => setQ(ev.target.value)}
                placeholder={t('Search variables')}
              />
            </TokenSearch>
            {!tokenGroups.length && (
              <EmptyTokenGroups>
                {t('No variables match your search.')}
              </EmptyTokenGroups>
            )}
            {tokenGroups.map((g) => (
              <TokenGroup key={g.key}>
                <TokenGroupHeading>
                  <Typography variant="h6">{g.label}</Typography>
                </TokenGroupHeading>
                <FlexContainer wrap="wrap">
                  {g.tokens.map((t) => (
                    <ButtonBase
                      key={t.token}
                      onClick={() => {
                        // TODO - finish date format dialog, then switch to the commented out code below
                        if (t.type === 'date') {
                          setState({ page: 'DATE', t });
                        } else if (t.type === 'number') {
                          setState({ page: 'NUMBER', t });
                        } else {
                          onSelect(t, '');
                        }
                      }}
                    >
                      <ColoredChip
                        key={t.token}
                        color={t.color}
                        variant="squared"
                        size="small"
                        mode="light"
                      >
                        {t.label}
                      </ColoredChip>
                    </ButtonBase>
                  ))}
                </FlexContainer>
              </TokenGroup>
            ))}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={onClose}>
              {t('Cancel')}
            </Button>
          </DialogActions>
        </>
      )}
      {state.page === 'DATE' && (
        <>
          <DialogContent>
            <div>
              {DATE_TIME_FORMATS.map((group, i) => (
                <FormatGroup key={i}>
                  {group.formats.map((f) => (
                    <FormatItem
                      key={f}
                      onClick={() => {
                        onSelect(state.t, f);
                        setState({ page: 'LIST' });
                      }}
                    >
                      <FlexContainer>
                        <Typography variant="body1">
                          {moment().format(f)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {f}
                        </Typography>
                      </FlexContainer>
                    </FormatItem>
                  ))}
                </FormatGroup>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setState({ page: 'LIST' })}
            >
              {t('Cancel')}
            </Button>
          </DialogActions>
        </>
      )}

      {state.page === 'NUMBER' && (
        <>
          <DialogContent>
            <FormatGroup>
              <FormatItem
                onClick={() => {
                  onSelect(state.t, '');
                  setState({ page: 'LIST' });
                }}
              >
                <FlexContainer>
                  <Typography variant="body1">1</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('count')}
                  </Typography>
                </FlexContainer>
              </FormatItem>

              <FormatItem
                onClick={() => {
                  onSelect(state.t, 'ordinal');
                  setState({ page: 'LIST' });
                }}
              >
                <FlexContainer>
                  <Typography variant="body1">{toOrdinal(1)}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('ordinal')}
                  </Typography>
                </FlexContainer>
              </FormatItem>
            </FormatGroup>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setState({ page: 'LIST' })}
            >
              {t('Cancel')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
// To prevent it from jumping while searching
const FixedHeightDialogContent = styled(DialogContent)((p) => ({
  height: '70vh'
}));

export const TokenSyntaxHelpDialog = ({
  open,
  onClose,
  tokens
}: {
  open: boolean;
  onClose: () => void;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState<
    | {
        page: 'LIST';
      }
    | {
        page: 'DATE';
        token: TokenDefinition;
      }
    | {
        page: 'NUMBER';
        token: TokenDefinition;
      }
    | {
        page: 'FORMAT';
        token: TokenDefinition;
        format: string;
      }
  >({ page: 'LIST' });

  const showFormatPage = (token: TokenDefinition, format: string) => {
    setState({
      page: 'FORMAT',
      token,
      format
    });
  };
  const [q, setQ] = useState('');
  useEffect(() => {
    open && setQ('');
  }, [open]);

  const tokenGroups = useMemo(() => {
    const re = toSearchTermRegexp(q);
    return Object.entries(
      groupBy(
        tokens.filter((t) => {
          // TODO use fuzzy matching, so that i can do pafi to find participant.firstName
          return t.token.match(re) || t.label.match(re);
        }),
        (t) => t.group
      )
    ).map(([group, ts]) => ({ key: group, label: group, tokens: ts }));
  }, [q, tokens]);

  const close = () => {
    setState({ page: 'LIST' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {state.page === 'LIST'
          ? t('Variables to insert in your template')
          : state.page === 'DATE'
          ? t('Choose a date format')
          : state.page === 'NUMBER'
          ? t('Choose a number format')
          : state.page === 'FORMAT'
          ? t('Fromatted variable')
          : assertNever(state)}
      </DialogTitleWithTypography>
      {state.page === 'LIST' && (
        <>
          <FixedHeightDialogContent>
            <FormRow>
              <Typography variant="body1">
                {t(
                  'Use the text below (including the { } characters) in your template, to indicate where Dado should insert data about employees.'
                )}
              </Typography>
            </FormRow>

            <TokenSearch>
              <TextField
                type="search"
                fullWidth
                autoFocus
                value={q}
                onChange={(ev) => setQ(ev.target.value)}
                placeholder={t('Search variables')}
              />
            </TokenSearch>
            {!tokenGroups.length && (
              <EmptyTokenGroups>
                {t('No variables match your search.')}
              </EmptyTokenGroups>
            )}
            {tokenGroups.map((g) => (
              <TokenGroup key={g.key}>
                <TokenGroupHeading>
                  <Typography variant="h6">{g.label}</Typography>
                </TokenGroupHeading>
                <List>
                  {g.tokens.map((token) => (
                    <ListItem
                      key={token.token}
                      style={
                        token.type === 'date' || token.type === 'number'
                          ? { cursor: 'pointer' }
                          : {}
                      }
                      onClick={() => {
                        // TODO - finish date format dialog, then switch to the commented out code below
                        if (token.type === 'date') {
                          setState({ page: 'DATE', token: token });
                        } else if (token.type === 'number') {
                          setState({ page: 'NUMBER', token: token });
                        }
                      }}
                    >
                      <FlexContainer>
                        <ColoredChip
                          key={token.token}
                          color={token.color}
                          variant="squared"
                          size="small"
                          mode="light"
                        >
                          {token.label}
                        </ColoredChip>
                        <Typography variant="body1">{`{{ ${token.token} }}`}</Typography>
                        <CopyButton
                          text={`{{ ${token.token} }}`}
                          successMessage="Variable copied"
                        />
                      </FlexContainer>
                    </ListItem>
                  ))}
                </List>
              </TokenGroup>
            ))}
          </FixedHeightDialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={close}>
              {t('Close')}
            </Button>
          </DialogActions>
        </>
      )}
      {state.page === 'DATE' && (
        <>
          <DialogContent>
            <div>
              {DATE_TIME_FORMATS.map((group, i) => (
                <FormatGroup key={i}>
                  {group.formats.map((f) => (
                    <FormatItem
                      key={f}
                      onClick={() => {
                        showFormatPage(state.token, f);
                      }}
                    >
                      <FlexContainer>
                        <Typography variant="body1">
                          {moment().format(f)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {f}
                        </Typography>
                      </FlexContainer>
                    </FormatItem>
                  ))}
                </FormatGroup>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setState({ page: 'LIST' })}
            >
              {t('Back')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setState({ page: 'LIST' })}
            >
              {t('Cancel')}
            </Button>
          </DialogActions>
        </>
      )}

      {state.page === 'NUMBER' && (
        <>
          <DialogContent>
            <FormatGroup>
              <FormatItem
                onClick={() => {
                  showFormatPage(state.token, '');
                }}
              >
                <FlexContainer>
                  <Typography variant="body1">1</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('count')}
                  </Typography>
                </FlexContainer>
              </FormatItem>

              <FormatItem
                onClick={() => {
                  showFormatPage(state.token, 'ordinal');
                  setState({ page: 'LIST' });
                }}
              >
                <FlexContainer>
                  <Typography variant="body1">{toOrdinal(1)}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('ordinal')}
                  </Typography>
                </FlexContainer>
              </FormatItem>
            </FormatGroup>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setState({ page: 'LIST' })}
            >
              {t('Back')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setState({ page: 'LIST' })}
            >
              {t('Cancel')}
            </Button>
          </DialogActions>
        </>
      )}

      {state.page === 'FORMAT' && (
        <>
          <DialogContent>
            <FlexContainer>
              <ColoredChip
                key={state.token.token}
                color={state.token.color}
                variant="squared"
                size="small"
                mode="light"
              >
                {state.token.label}
              </ColoredChip>
              <Typography variant="body1">{`{{ ${state.token.token} ${
                state.format ? `| ${state.format} ` : ''
              } }}`}</Typography>
              <CopyButton
                text={`{{ ${state.token.token} ${
                  state.format ? `| ${state.format} ` : ''
                } }}`}
                successMessage="Formatted variable copied"
              />
            </FlexContainer>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setState({ page: 'LIST' })}
            >
              {t('Back')}
            </Button>
            <Button variant="outlined" onClick={close}>
              {t('Close')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export const MarkdownEditorWithReplacementTokens = React.forwardRef<
  ContextStore,
  MarkdownEditorProps & { tokens: TokenDefinition[] }
>(({ tokens, ...props }, ref) => {
  const { t } = useUnsafeTranslation();
  const [dialogState, setDialogState] = useState<{
    state: TextState;
    api: TextAreaTextApi;
  } | null>(null);
  const close = useMemo(() => () => setDialogState(null), []);
  const { disabled } = props;
  const extraCommands: ICommand[] = useMemo(
    () => [
      {
        name: 'token',
        keyCommand: 'token',
        buttonProps: { 'aria-label': t('Insert Text Variable'), disabled },
        icon: (
          <ToolbarIconLabelWithIcon>
            <div>
              <PlusSquare size={12} />
            </div>
            <div>{t('Text Variable')}</div>
          </ToolbarIconLabelWithIcon>
        ),
        execute: (state: TextState, api: TextAreaTextApi) => {
          setDialogState({ state, api });
        }
      }
    ],
    [disabled, t]
  );

  // We probably should handle onBlur here, so that it doesn't trigger
  // if we go to the dialog state - but it's such a hassle to implement.
  // Not worth it...
  return (
    <>
      <MarkdownEditor {...props} ref={ref} extraCommands={extraCommands} />
      <TokenSelectorDialog
        open={!!dialogState}
        onClose={() => {
          if (dialogState) {
            // reposition cursor and refocus
            setTimeout(() => {
              dialogState.api.setSelectionRange(dialogState.state.selection);
            });
          }
          close();
        }}
        tokens={tokens}
        onSelect={(t, opts) => {
          if (!dialogState) {
            return;
          }
          setTimeout(() => {
            close();
            const token = `{{ ${[t.token, opts]
              .filter((x) => x)
              .join(' | ')} }}`;
            // also focuses
            dialogState.api.replaceSelection(token);
          });
        }}
      />
    </>
  );
});

const MDContainer = styled('div')<{
  focused: boolean;
  disabled: boolean;
  error?: boolean;
  fullWidth?: boolean;
  textAlign?: 'left' | 'center' | 'right';
}>((p) => {
  const ed = '.w-md-editor';
  return {
    width: p.fullWidth ? '100%' : 'auto',
    [ed]: {
      borderRadius: 4,
      borderColor: p.error
        ? p.theme.palette.error.main
        : p.focused
        ? p.theme.palette.primary.main
        : p.theme.palette.grey[300],
      borderWidth: 1,
      borderStyle: 'solid',
      boxShadow: p.focused
        ? `0 0 0 1px ${
            p.error ? p.theme.palette.error.main : p.theme.palette.primary.main
          }, 0 0 0 4px rgba(103,178,244,.1)`
        : 'none',
      backgroundColor: p.theme.palette.common.white,
      boxSizing: 'border-box',
      // The editor applies `font-family: var(--md-editor-font-family) !important`
      // to the textarea and the highlighted-overlay <pre>. The default value
      // of that variable is Helvetica Neue, so any rule we set on the textarea
      // (without `!important`) loses to it. Overriding the variable on the
      // editor root is cleaner than peppering `!important` on every consumer.
      '--md-editor-font-family': 'ebony, sans-serif'
    },
    [ed + ':hover']: {
      borderColor: p.disabled
        ? p.theme.palette.grey[300]
        : p.focused
        ? p.theme.palette.primary.main
        : p.theme.palette.grey[400],
      boxShadow: p.disabled
        ? 'none'
        : p.focused
        ? `0 0 0 1px ${p.theme.palette.primary.main}, 0 0 0 4px rgba(103,178,244,.1)`
        : '0 0 0 4px rgba(119,128,136,.1)'
    },
    [ed + '-content']: {
      padding: p.theme.spacing(0.5)
    },
    [ed + '-text']: {
      fontFamily: 'ebony,sans-serif',
      fontSize: p.theme.custom.fontSize.m,
      borderColor: p.theme.palette.primary.main,
      lineHeight: 1.6,
      textAlign: p.textAlign || 'left',
      // Newer @uiw/react-md-editor inlines `min-height: <minHeight>px` (default 100)
      // here, which floored short editors at 100px (legacy had no such floor). Fill
      // the height-driven container (`.w-md-editor` is sized by the `height` prop =
      // rows*24, default 200) so the editor sizes by `rows` like legacy — no fixed
      // px floor, no collapse, no overflow. `!important` beats the inline style.
      minHeight: '100% !important'
    },
    [ed + '-text textarea']: {
      // Newer @uiw/react-md-editor drops the `<pre>` syntax-highlight
      // overlay used by legacy and inlines `-webkit-text-fill-color: initial`
      // on the textarea so its raw text is visible. The textarea then needs
      // its own font + color rules — font-family is sourced from the
      // overridden CSS variable on `.w-md-editor`; font-size and line-height
      // are set directly (the editor's own `!important` rule sets them on
      // the parent only, so a direct rule on the textarea still wins);
      // color is explicit because the editor's CSS defaults the foreground
      // to a muted GitHub `--color-fg-default` that doesn't match the rest
      // of the admin UI.
      textAlign: p.textAlign || 'left',
      fontSize: p.theme.custom.fontSize.m,
      lineHeight: 1.6,
      // `!important` on color: the editor's own CSS has
      // `.w-md-editor-text-input { color: transparent !important; }` inside
      // an @supports block that activates in Chrome/Safari/Firefox — without
      // !important the textarea text would render transparent and rely on
      // the now-disabled <pre> overlay.
      color: `${p.theme.palette.text.primary} !important`
    },
    ...(p.disabled
      ? {
          '.language-markdown, .token, .wmde-markdown-color': {
            // might need more overrides here
            // wmde-markdown-color overrides when highlighting is disabled
            // the others override the highlighting
            color: `${p.theme.palette.grey[400]} !important`
          }
        }
      : {}),
    [ed + '-toolbar']: {
      padding: `${p.theme.spacing(1)}px ${p.theme.spacing(1)}px`,
      fontFamily: 'ebony,sans-serif'
    }
  };
});

// NEW_UX inline text editor chrome — mirrors the task-editor prototype (proto-app.jsx
// RichToolbar/ContentBlockVisual): no box border (the block's selection ring is drawn by
// the surrounding BlockItem), a light toolbar that shows only while the editor is focused,
// and the prototype's body typography. Same markdown commands as the classic editor.
const NewUxMDContainer = styled('div')<{
  focused: boolean;
  fullWidth?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  backgroundColor?: string;
}>((p) => {
  const ed = '.w-md-editor';
  return {
    width: p.fullWidth ? '100%' : 'auto',
    [ed]: {
      border: 'none',
      boxShadow: 'none',
      backgroundColor: p.backgroundColor || p.theme.palette.common.white,
      '--md-editor-font-family': 'ebony, sans-serif'
    },
    // Light, prototype-style toolbar. Spans the full block width and stays visible
    // while the editor is mounted (the editor mounts only for the selected block, so
    // this reads as the prototype's "toolbar on the focused block"). It must NOT hide
    // on blur — clicking a toolbar button blurs the textarea, and a display:none-on-blur
    // toolbar would vanish mid-click and swallow the command (e.g. Text Variable).
    [ed + '-toolbar']: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      padding: '6px 16px',
      background: '#FAFAFA',
      borderBottom: '1px solid #EBEBEB',
      fontFamily: 'ebony, sans-serif'
    },
    [ed + '-toolbar ul']: { display: 'flex', alignItems: 'center' },
    [ed + '-toolbar li > button']: {
      color: '#374151',
      height: 26,
      minWidth: 26,
      borderRadius: 4,
      background: 'transparent'
    },
    [ed + '-toolbar li > button:hover']: {
      background: '#EBF3FE',
      color: '#2563EB'
    },
    [ed + '-content']: { padding: 0 },
    [ed + '-text']: {
      fontFamily: 'ebony,sans-serif',
      fontSize: 15,
      lineHeight: 1.6,
      minHeight: '100% !important',
      padding: '12px 32px',
      textAlign: p.textAlign || 'left'
    },
    [ed + '-text textarea']: {
      fontSize: 15,
      fontWeight: 300,
      lineHeight: 1.6,
      color: `${p.theme.palette.text.primary} !important`,
      textAlign: p.textAlign || 'left'
    },
    [ed + '-text textarea::placeholder']: { color: '#B0B8C4' }
  };
});

export const MarkdownEditor = React.forwardRef<
  ContextStore,
  MarkdownEditorProps & { extraCommands?: ICommand[] }
>(
  (
    {
      value,
      onChange,
      extraCommands = EMPTY_ARR,
      disabled,
      autoFocus,
      helperText,
      error,
      rows,
      onBlur,
      fullWidth,
      textAlign = 'left',
      newUx,
      placeholder,
      backgroundColor
    },
    ref
  ) => {
    const customCommands: ICommand[] = useMemo(
      () =>
        [
          commands.bold,
          commands.italic,
          commands.divider,
          commands.link,
          commands.divider,
          commands.unorderedListCommand,
          commands.orderedListCommand
        ].map((c) =>
          disabled
            ? {
                ...c,
                buttonProps: { ...c.buttonProps, disabled }
              }
            : c
        ),
      [disabled]
    );
    const [focused, setFocused] = useState(false);
    const textAreaProps = useMemo(
      () => ({
        disabled,
        autoFocus,
        rows,
        placeholder
      }),
      [disabled, autoFocus, rows, placeholder]
    );

    const editor = (
      <MDEditor
        ref={ref}
        value={value}
        onChange={(v) => onChange(v || '')}
        preview="edit"
        commands={customCommands}
        extraCommands={extraCommands}
        onFocus={() => setFocused(true)}
        onBlur={(ev) => {
          onBlur && onBlur(ev);
          setFocused(false);
        }}
        textareaProps={textAreaProps}
        highlightEnable={false}
        height={rows ? rows * 24 : undefined}
      />
    );

    if (newUx) {
      return (
        <NewUxMDContainer
          focused={focused}
          fullWidth={fullWidth}
          textAlign={textAlign}
          backgroundColor={backgroundColor}
          data-color-mode="light"
        >
          {editor}
          {helperText && (
            <FormHelperText error={error}>{helperText}</FormHelperText>
          )}
        </NewUxMDContainer>
      );
    }

    return (
      <MDContainer
        focused={focused}
        disabled={disabled || false}
        error={error}
        fullWidth={fullWidth}
        textAlign={textAlign}
      >
        {editor}
        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </MDContainer>
    );
  }
);

export const truncateMarkdown = (d: Markdown, limit = 100) => {
  return {
    ...d,
    t: _truncateMarkdown(d.t, { limit, ellipsis: true })
  };
};
